import React from "react";
import {
  QUEST_ACTION_OPTIONS,
  QUEST_TABLE_GRID_COLUMNS,
  QUEST_TARGET_ROLE_OPTIONS,
} from "common/constants";
import type { AdminQuest } from "common/types/questManagement.type";
import {
  formatQuestActiveMonth,
  isDefaultHiddenQuest,
} from "common/questManagement.utils";

interface QuestTableProps {
  title: string;
  quests: AdminQuest[];
  isLoading: boolean;
  errorMessage: string;
  onRetry: () => void;
  onCreateClick: () => void;
  onEditClick: (quest: AdminQuest) => void;
  onToggleActiveClick: (quest: AdminQuest) => void;
  onDeleteClick: (quest: AdminQuest) => void;
  togglingQuestIds: Set<string>;
  isCreateDisabled: boolean;
}

const QUEST_ACTION_LABELS = QUEST_ACTION_OPTIONS.reduce<Record<string, string>>(
  (labels, option) => {
    labels[option.value] = option.label;
    return labels;
  },
  {},
);

const QUEST_TARGET_ROLE_LABELS = QUEST_TARGET_ROLE_OPTIONS.reduce<
  Record<string, string>
>((labels, option) => {
  labels[option.value] = option.label;
  return labels;
}, {});

const formatQuestRule = (quest: AdminQuest) => {
  if (quest.description) return quest.description;

  const actionLabel = QUEST_ACTION_LABELS[quest.action_type];
  if (actionLabel) {
    return `${actionLabel}を${quest.target_count}回達成する。`;
  }

  return `目標を${quest.target_count}回達成する。`;
};

const QuestTable = ({
  title,
  quests,
  isLoading,
  errorMessage,
  onRetry,
  onCreateClick,
  onEditClick,
  onToggleActiveClick,
  onDeleteClick,
  togglingQuestIds,
  isCreateDisabled,
}: QuestTableProps) => {
  const renderRows = () => {
    if (isLoading) {
      return (
        <div className="flex min-h-[120px] items-center justify-center border-x border-b border-[#d5d5d5] text-[13px] text-[#1b4b86]">
          クエストデータを読み込み中です。
        </div>
      );
    }

    if (errorMessage) {
      return (
        <div className="flex min-h-[120px] flex-col items-center justify-center gap-[10px] border-x border-b border-[#d5d5d5] text-center">
          <p className="text-[13px] font-bold text-[#1b4b86]">{errorMessage}</p>
          <button
            className="h-[30px] rounded-[6px] bg-[#1b4b86] px-[15px] text-[12px] font-bold text-white"
            onClick={onRetry}
            type="button"
          >
            再読み込み
          </button>
        </div>
      );
    }

    if (quests.length === 0) {
      return (
        <div className="flex min-h-[120px] flex-col items-center justify-center gap-[8px] border-x border-b border-[#d5d5d5] text-center">
          <p className="text-[16px] font-bold text-[#1b4b86]">
            クエストデータがありません
          </p>
          <p className="text-[13px] text-black">
            現在、表示できるクエストデータがありません。
          </p>
        </div>
      );
    }

    return quests.map((quest, index) => {
      const isLocked = isDefaultHiddenQuest(quest);

      return (
        <div
          key={quest.id}
          className={`grid min-h-[41px] ${QUEST_TABLE_GRID_COLUMNS} text-[13px] leading-[1.6] text-black ${
            index % 2 === 1 ? "bg-[#f9fcfe]" : "bg-white"
          }`}
        >
          <div className="border-x border-b border-[#d5d5d5] px-[15px] py-[10px]">
            {quest.title}
          </div>
          <div className="border-b border-r border-[#d5d5d5] px-[15px] py-[10px]">
            {formatQuestRule(quest)}
          </div>
          <div className="flex h-full items-center justify-center whitespace-nowrap border-b border-r border-[#d5d5d5] px-[10px] py-[10px] text-center">
            {QUEST_TARGET_ROLE_LABELS[quest.target_role] ?? "すべて"}
          </div>
          <div className="flex h-full items-center justify-center whitespace-nowrap border-b border-r border-[#d5d5d5] px-[10px] py-[10px] text-center">
            {formatQuestActiveMonth(quest.active_start_at)}
          </div>
          <div className="flex h-full items-center justify-center whitespace-nowrap border-b border-r border-[#d5d5d5] px-[10px] py-[10px] text-center">
            {formatQuestActiveMonth(quest.active_end_at)}
          </div>
          <div className="flex h-full items-center justify-center whitespace-nowrap border-b border-r border-[#d5d5d5] px-[10px] py-[10px] text-center">
            {quest.is_active ? "有効" : "無効"}
          </div>
          <div className="flex h-full items-center justify-center border-b border-r border-[#d5d5d5] px-[15px] py-[10px]">
            <div className="flex items-center justify-center gap-[12px] leading-[1.6]">
              <button
                className="text-[13px] leading-[1.6] text-[#1b4b86] underline"
                onClick={() => onEditClick(quest)}
                type="button"
              >
                編集
              </button>
              <button
                className="text-[13px] leading-[1.6] text-[#1b4b86] underline disabled:text-[#adbbcc]"
                disabled={togglingQuestIds.has(String(quest.id))}
                onClick={() => onToggleActiveClick(quest)}
                type="button"
              >
                {quest.is_active ? "凍結" : "凍結解除"}
              </button>
              <button
                className="text-[13px] leading-[1.6] text-[#de3c3f] underline disabled:text-[#adbbcc]"
                disabled={isLocked}
                onClick={() => onDeleteClick(quest)}
                type="button"
              >
                削除
              </button>
            </div>
          </div>
        </div>
      );
    });
  };

  const renderMobileCards = () => {
    if (isLoading) {
      return (
        <div className="flex min-h-[120px] items-center justify-center rounded-md border border-[#d5d5d5] text-[13px] text-[#1b4b86]">
          クエストデータを読み込み中です。
        </div>
      );
    }

    if (errorMessage) {
      return (
        <div className="flex min-h-[120px] flex-col items-center justify-center gap-2.5 rounded-md border border-[#d5d5d5] text-center">
          <p className="text-[13px] font-bold text-[#1b4b86]">{errorMessage}</p>
          <button
            className="h-[30px] rounded-md bg-[#1b4b86] px-[15px] text-xs font-bold text-white"
            onClick={onRetry}
            type="button"
          >
            再読み込み
          </button>
        </div>
      );
    }

    if (quests.length === 0) {
      return (
        <div className="flex min-h-[120px] flex-col items-center justify-center gap-2 rounded-md border border-[#d5d5d5] px-4 text-center">
          <p className="text-base font-bold text-[#1b4b86]">
            クエストデータがありません
          </p>
          <p className="text-[13px] text-black">
            現在、表示できるクエストデータがありません。
          </p>
        </div>
      );
    }

    return quests.map((quest) => {
      const isLocked = isDefaultHiddenQuest(quest);

      return (
        <article
          key={quest.id}
          className="overflow-hidden rounded-md border border-[#d5d5d5] bg-white"
        >
          <h4 className="flex min-h-[42px] items-center justify-center bg-[#1b4b86] px-4 py-2 text-center text-sm font-bold leading-[1.6] text-white">
            {quest.title}
          </h4>
          <div className="p-5">
            <div>
              <p className="text-sm leading-[1.6] text-[#adbbcc]">
                条件 / ルール
              </p>
              <p className="mt-1 text-sm leading-[1.6] text-black">
                {formatQuestRule(quest)}
              </p>
            </div>
            <div className="mt-4">
              <p className="text-sm leading-[1.6] text-[#adbbcc]">対象</p>
              <p className="mt-1 text-sm leading-[1.6] text-black">
                {QUEST_TARGET_ROLE_LABELS[quest.target_role] ?? "すべて"}
              </p>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm leading-[1.6] text-[#adbbcc]">開始月</p>
                <p className="mt-1 text-sm leading-[1.6] text-black">
                  {formatQuestActiveMonth(quest.active_start_at)}
                </p>
              </div>
              <div>
                <p className="text-sm leading-[1.6] text-[#adbbcc]">終了月</p>
                <p className="mt-1 text-sm leading-[1.6] text-black">
                  {formatQuestActiveMonth(quest.active_end_at)}
                </p>
              </div>
            </div>
            <div className="mt-4">
              <p className="text-sm leading-[1.6] text-[#adbbcc]">状態</p>
              <p className="mt-1 text-sm leading-[1.6] text-black">
                {quest.is_active ? "有効" : "無効"}
              </p>
            </div>
            <button
              className="mt-5 flex h-[50px] w-full items-center justify-center rounded-md bg-[#eef5fa] text-base font-bold text-[#1b4b86]"
              onClick={() => onEditClick(quest)}
              type="button"
            >
              編集する
            </button>
            <div className="mt-2.5 flex items-center justify-center gap-8">
              <button
                className="text-sm leading-[1.6] text-[#1b4b86] underline disabled:text-[#adbbcc]"
                disabled={togglingQuestIds.has(String(quest.id))}
                onClick={() => onToggleActiveClick(quest)}
                type="button"
              >
                {quest.is_active ? "凍結する" : "凍結解除する"}
              </button>
              <button
                className="text-sm leading-[1.6] text-[#de3c3f] underline disabled:text-[#adbbcc]"
                disabled={isLocked}
                onClick={() => onDeleteClick(quest)}
                type="button"
              >
                削除する
              </button>
            </div>
          </div>
        </article>
      );
    });
  };

  return (
    <div className="mt-5 sm:mt-10">
      <div className="mb-5 flex min-h-10 items-center justify-between gap-4">
        <h3 className="flex-1 text-lg font-bold leading-[1.6] text-[#1b4b86] sm:h-8 sm:text-xl">
          {title}
        </h3>
        <button
          aria-disabled={isCreateDisabled}
          className={`flex h-[40px] w-[121px] shrink-0 items-center justify-center gap-[5px] rounded-[6px] px-[20px] text-[14px] font-bold leading-[1.6] text-white ${
            isCreateDisabled ? "bg-[#adbbcc]" : "bg-[#1b4b86]"
          }`}
          onClick={onCreateClick}
          type="button"
        >
          <span className="flex h-[20px] w-[20px] items-center justify-center text-[20px] font-normal leading-none">
            +
          </span>
          <span>新規作成</span>
        </button>
      </div>

      <div className="grid gap-[15px] sm:hidden">{renderMobileCards()}</div>

      <div className="hidden overflow-x-auto sm:block">
        <div className="w-full min-w-[1240px]">
          <div
            className={`grid h-[42px] ${QUEST_TABLE_GRID_COLUMNS} overflow-hidden rounded-t-[6px] bg-[#1b4b86] text-[14px] font-bold leading-[1.6] text-white`}
          >
            <div className="border-r border-white/30 px-[15px] py-[10px] text-center">
              クエスト名
            </div>
            <div className="border-r border-white/30 px-[15px] py-[10px] text-center">
              条件 / ルール
            </div>
            <div className="border-r border-white/30 px-[15px] py-[10px] text-center">
              対象
            </div>
            <div className="border-r border-white/30 px-[15px] py-[10px] text-center">
              開始月
            </div>
            <div className="border-r border-white/30 px-[15px] py-[10px] text-center">
              終了月
            </div>
            <div className="border-r border-white/30 px-[15px] py-[10px] text-center">
              状態
            </div>
            <div className="px-[15px] py-[10px] text-center">操作</div>
          </div>
          {renderRows()}
        </div>
      </div>
    </div>
  );
};

export default QuestTable;
